/** lag-geolocation-input {
 *      onlyCountryProvince: If all fields should be hidden except country & province.
 *      showAddressUnit: If the address unit field should be shown (hidden by default), not applicable if onlyCountryProvince is true.
 * 
 *      disabled: If the inputs are disabled.
 *      required: If it is a required input.
 *      autofocus: Attempts to auto-focus the address input box.
 *
 *      invalid: If any of the fields are considered invalid.*
 *
 *      addressPlace: A google maps place object containing info from autocomplete API.
 *      coords: A google LatLng object (if autocomplete was used and was valid).
 *
 *      address: Street address string.
 *      addressUnit: Apartment/department/etc string.
 *      city: City sring.
 *
 *      postalCode: PostalCode string.
 *
 *      subRegion: The subRegion's name string (if subRegion object is set then this will be the same as subRegionLocation.name).
 *      subRegionLocationId: The subRegion's LocationID.
 *      subRegionLocation: The subRegion Location object (if selected by user, and when appropriate for province).
 *
 *      province: The province's name string (if province object is set then this will be the same as provinceLocation.name).
 *      provinceLocationId: The province's LocationID.
 *      provinceLocation: The province Location object (if selected by user, and when appropriate for country).
 *
 *      country: The country's name string (if country object is set then this will be the same as countryLocation.name).
 *      countryLocationId: The country's LocationID.
 *      countryLocation: The country's Location object (if selected by user).
 *  }
 *
 *  <lag-geolocation-input disabled required autofocus only-country-province show-address-unit></lag-geolocation-input>
 */

// TODO: subRegion doesnt set on initial load due to on provincelocationid changed not being triggered at the right time to cause the subregion load.
// TODO: on country change the existing province value doesn't validate.
// TODO: store locations in localStorage for a period of time (eg 24h) so we dont need to request them on refresh, or is this unnecessary optimization?
// TODO: redo lag-geolocation-input it's a fragile confusion mess that doesnt work properly. think about how to design it well, consider all the events.
//       consider update looping: set ele.province, observer(province) updates ele.value, observer(value) updates province, ...
//       thoughts on redoing:
//          - observe(province/provinceLocationID), observe(country/countryLocationID)
//          - bind on provinceLocation, countryLocation, _provinceValue, _countryValue
//          - observe(provinceLocation/_provinceValue), observe(countryLocation/_countryValue)
//          - on changes/updates (in observers) have an update lock var so any observers triggered by changes are aborted without causing loops
//          - have one unified update function that observers call?


/* Google Maps Api
    REFERENCES
    ==========
    https://developers.google.com/maps/documentation/javascript/3.exp/reference#PlaceResult
    https://developers.google.com/maps/documentation/javascript/3.exp/reference#GeocoderAddressComponent
    https://developers.google.com/maps/documentation/geocoding/start#Types
    https://developers.google.com/maps/documentation/geocoding/intro#GeocodingResponses

    PLACE COMPONENT TYPES
    =====================
    street_number
    route                           // street name
    intersection
    subpremise                      // sub-division of premise
    premise                         // named building/complex/etc
    neighborhood
    sublocality_level_*             // detailed divisions of sublocality (1 to 5)
    sublocality                     // boroughs, etc
    ward                            // special Japanese identifier
    locality                        // city/town
    colloquial_area                 // local nickname for area
    administrative_area_level_*     // further administrative divisions (3 to 5)
    administrative_area_level_2     // county/etc
    administrative_area_level_1     // province
    country
    postal_code
    political                       // indicator of political entity (usually in addition to another type)
*/

import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { Localizer } from '../extendibles/Localizer.js';
import '@polymer/paper-input/paper-input.js';
import '@vaadin/vaadin-combo-box/theme/material/vaadin-combo-box.js';

import { loader as gmapsApiLoader } from '../../libs/google-maps-api.js';

import { defineElement } from '../../utility/defineElement.js';
import { api } from '../../api/api.js';
import { enqueue } from '../../utility/enqueue.js';

import { template } from './lag-geolocation-input.html.js';

// Shared "static" objects between instances.
let GMAPSAPI;
const SHARED = {
    locations: null, // Set in _loadLocations
    countriesRequiringPostalCodes: ['ca', 'us'] // Country codes, when one of these countries is selected the postalCode will be required.
};

defineElement(class extends Localizer(PolymerElement) {

    static get is() { return 'lag-geolocation-input'; }

    static get template() { return template; }

    static get properties() {
        return {
            onlyCountryProvince: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            },
            //showAddressUnit: Boolean, // This is used in html & css.

            // Set to true to disable this input.
            disabled: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            },
            // Set to true to mark the input as required.
            required: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            },
            // Autofocus input box.
            autofocus: Boolean,

            // If if the input box value is indicated as invalid.
            invalid: {
                type: Boolean,
                value: false
            },

            // Google Maps place object provided by Autocomplete API (set in _onAddressAutocomplete).
            addressPlace: {
                type: Object,
                value: _ => {}
            },

            // The LatLng coords for the place, if available.
            coords: {
                type: Object,
                computed: '_computeCoords(addressPlace)'
            },

            address: {
                type: String,
                notify: true
            },
            addressUnit: {
                type: String,
                notify: true
            },
            city: {
                type: String,
                notify: true
            },

            postalCode: {
                type: String,
                notify: true
            },

            subRegion: { // Get/set text value for province, if a matching location choice exists then subRegionLocation.name will be the same.
                type: String,
                notify: true,
                value: null
            },
            subRegionLocationId: { // Same as subRegionLocation.locationID, but allows easy access and ability to set location by id.
                type: Number,
                value: null,
                observer: '_observeSubRegionLocationId'
            },
            subRegionLocation: { // Get the subRegion location object data (when available).
                type: Object,
                notify: true
            },

            province: { // Get/set text value for province, if a matching location choice exists then provinceLocation.name will be the same.
                type: String,
                notify: true,
                value: null
            },
            provinceLocationId: { // Same as provinceLocation.locationID, but allows easy access and ability to set location by id.
                type: Number,
                value: null,
                observer: '_observeProvinceLocationId'
            },
            provinceLocation: { // Get the province location object data (when available).
                type: Object,
                notify: true
            },

            country: { // Get/set text value for country, if a matching location choice exists then countryLocation.name will be the same.
                type: String,
                notify: true,
                value: null
            },
            countryLocationId: { // Same as countryLocation.locationID, but allows easy access and ability to set location by id.
                type: Number,
                value: null,
                observer: '_observeCountryLocationId'
            },
            countryLocation: { // Bound to the selected country location object data (when available).
                type: Object,
                notify: true
            },

            _requireNonCountryProvince: {
                type: Boolean,
                computed: '_computeRequireNonCountryProvince(required, onlyCountryProvince)'
            },
            _requirePostalCode: {
                type: Boolean,
                computed: '_computeRequirePostalCode(required, onlyCountryProvince, countryLocation)'
            },

            _hideNonCountryProvince: {
                type: Boolean,
                computed: '_computeHideNonCountryProvince(onlyCountryProvince)'
            },
            _showSubRegion: {
                type: Boolean,
                value: false
            },
            _gApiSessionToken: String
        };
    }

    static get observers() {
        return [
            '_onAddressChanged(address)',
            '_onCityChanged(city)',
            '_onSubRegionChanged(subRegion)',
            '_onProvinceChanged(province)',
            '_onPostalCodeChanged(postalCode)',
            '_onCountryChanged(country)'
        ];
    }

    ready() {
        super.ready();

        this._generateNewSessionToken();
        this._loadLocations();

        // Run on*Changed methods to cause initial validation checks.
        this._onAddressChanged();
        this._onCityChanged();
        this._onSubRegionChanged();
        this._onProvinceChanged();
        this._onPostalCodeChanged();
        this._onCountryChanged();

        // Disable gmail api for now. as instructed by jonagh.
        gmapsApiLoader.then(this._onApiLoad.bind(this));
    }

    _computeRequireNonCountryProvince(required, onlyCountryProvince) {
        return required && !onlyCountryProvince;
    }
    _computeHideNonCountryProvince(onlyCountryProvince) {
        return !!onlyCountryProvince;
    }
    _computeRequirePostalCode(required, onlyCountryProvince, countryLocation) {
        return required && !onlyCountryProvince && countryLocation && SHARED.countriesRequiringPostalCodes.includes(countryLocation.code);
    }

    _loadLocations() {
        enqueue('lag-geolocation-input._loadLocations', () => {
            if (!SHARED.locations || !SHARED.locations.length) {
                return api.get('locations').then(result => {
                    SHARED.locations = result.data.locations;
                    this._loadCountries();
                })
                .catch(_ex_ => {
                    // TODO: handle failures
                });
            }
            else {
                this._loadCountries();
            }

            // Note: The province (and possibly subRegion) listing will be loaded when the country selection changes.
        });
    }
    _loadCountries() {
        if (SHARED.locations) {
            if (!this.$.inputCountry.items || !this.$.inputCountry.items.length) {
                // Countries have parentLocationID set to 0 or null.
                this.$.inputCountry.items = SHARED.locations.filter((i) => !i.parentLocationID);

                // If no country value set but there is a countryLocationId then trigger the observer
                // so the location id lookup occurs and sets the country appropriatly; ie the location id
                // was set before these countries were loaded, so now we have to trigger the set code again.
                if ((!this.country || this.country.length === 0) && this.countryLocationId) {
                    this._observeCountryLocationId(this.countryLocationId);
                }
                else this._onCountryChanged(); // Call to cause validity check.
            }
        }
    }
    _loadProvinces(countryId) {
        if (SHARED.locations) {
            // Load provinces (for country) into province chooser.
            this.$.inputProvince.items = (!countryId) ? null :
                SHARED.locations.filter((i) => i.parentLocationID === countryId);

            // If there are province choices then set required appropriately, otherwise don't require (not all countries have provinces).
            if (this.$.inputProvince.items && this.$.inputProvince.items.length) {
                this.$.inputProvince.required = this.required;
            }
            else this.$.inputProvince.required = false;

            // If no province value set but there is a provinceLocationId then trigger the observer
            // so the location id lookup occurs and sets the province appropriatly; ie the location id
            // was set before these provinces were loaded, so now we have to trigger the set code again.
            if ((!this.province || this.province.length === 0) && this.provinceLocationId) {
                this._observeProvinceLocationId(this.provinceLocationId);
            }
            else this._onProvinceChanged(); // Call to cause validity check.
        }
    }
    _loadSubRegions(provinceId) {
        if (SHARED.locations) {
            // Load subRegions (for province) into subRegions chooser.
            this.$.inputSubRegion.items = (!provinceId) ? null :
                SHARED.locations.filter((i) => i.parentLocationID === provinceId);

            // If there are subRegion choices then set required approrpiately, otherwise don't require (not all provinces have subRegions).
            // Also show/hide the subRegion, if none exist then we don't show it at all since we dont care unless choices exist.
            if (this.$.inputSubRegion.items && this.$.inputSubRegion.items.length) {
                this.$.inputSubRegion.required = this.required;
                this._showSubRegion = !this.onlyCountryProvince; // true: when NOT only-country-province.
            }
            else {
                this.$.inputSubRegion.required = false;
                this._showSubRegion = false;
            }

            // If no subRegion value set but there is a subRegionLocationId then trigger the observer
            // so the location id lookup occurs and sets the subRegion appropriatly; ie the location id
            // was set before these subRegions were loaded, so now we have to trigger the set code again.
            if ((!this.subRegion || this.subRegion.length === 0) && this.subRegionLocationId) {
                this._observeSubRegionLocationId(this.subRegionLocationId);
            }
            else this._onSubRegionChanged(); // Call to cause validity check.
        }
    }

    _observeSubRegionLocationId(locationId, old_locationId) {
        this._processLocationId(locationId, old_locationId, this.$.inputSubRegion);
    }
    _observeProvinceLocationId(locationId, old_locationId) {
        this._processLocationId(locationId, old_locationId, this.$.inputProvince);

        this._loadSubRegions(locationId || null);
    }
    _observeCountryLocationId(locationId, old_locationId) {
        this._processLocationId(locationId, old_locationId, this.$.inputCountry);
    }

    _processLocationId(locationId, old_locationId, locationElement) {
        if (!locationId || typeof locationId !== 'number') return;
        if (locationId === old_locationId) return;

        const locations = locationElement.items;
        if (!locations || !locations.length) return;

        const currentLocation = locationElement.selectedItem;
        if (currentLocation && currentLocation.locationID === locationId)
            return; // Already set to that id, nothing to do. 

        const foundLocation = locations.find((i) => i.locationID === locationId);
        if (foundLocation) {
            locationElement.selectedItem = foundLocation;
        }
        else {
            locationElement.value = '';
        }
    }

    _generateNewSessionToken() {
        // UUID v4 generator in JavaScript (RFC4122 compliant) in order to stay compliant with google api
        // source https://gist.github.com/jcxplorer/823878
        var uuid = "", i, random;
        for (i = 0; i < 32; i++) {
            random = Math.random() * 16 | 0;

            if (i == 8 || i == 12 || i == 16 || i == 20) {
                uuid += "-";
            }
            uuid += (i == 12 ? 4 : (i == 16 ? (random & 3 | 8) : random)).toString(16);
        }
        this._gApiSessionToken = uuid;
    }

    _onApiLoad(gmapsApi) {
        // https://developers.google.com/maps/documentation/javascript/3.exp/reference#Autocomplete
        // https://developers.google.com/maps/documentation/javascript/3.exp/reference#AutocompleteOptions

        GMAPSAPI = gmapsApi;

        this._autocompleters = {
            address: this._wireAutocomplete(this.$.inputAddress, this._onAddressAutocomplete, 'geocode'),
            city: this._wireAutocomplete(this.$.inputCity, this._onCityAutocomplete, '(cities)')
        };
    }
    _wireAutocomplete(paperInputBox, handler, autocompleteType) {
        const api = GMAPSAPI,
            nativeInput = this._extractNativeInputBox(paperInputBox),
            autocompleter = new api.places.Autocomplete(nativeInput, { types: [autocompleteType], sessiontoken: [this._gApiSessionToken] });

        api.event.addListener(autocompleter, 'place_changed', handler.bind(this));

        return autocompleter;
    }
    _extractNativeInputBox(paperInputBox) {
        return paperInputBox.shadowRoot.querySelector('input');
    }

    _onAddressAutocomplete() {
        this.addressPlace = this._autocompleters.address.getPlace();

        this._autocompleteFill(this.addressPlace.address_components);

        this.address = this._deriveAddressFromPlace(this.address, this.addressPlace);
    }
    _onCityAutocomplete() {
        const cityPlace = this._autocompleters.city.getPlace();

        this._autocompleteFill(cityPlace.address_components);
    }
    _autocompleteFill(address_components) {
        if (!address_components) return;

        const city = this._extractAddressComponent(address_components, 'locality')
                   || this._extractAddressComponent(address_components, 'administrative_area_level_3'),
            subRegion = this._extractAddressComponent(address_components, 'administrative_area_level_2'),
            province = this._extractAddressComponent(address_components, 'administrative_area_level_1'),
            postalCode = this._extractAddressComponent(address_components, 'postal_code'),
            country = this._extractAddressComponent(address_components, 'country');

        if (city) {
            this.city = city.long_name;
        }

        if (postalCode) {
            this.postalCode = postalCode.long_name;
        }

        // These need to be set in this order because they rely on each other,
        // eg country will set and then the province will load the correct provinces into the list,
        // if we were to set the province first it wouldn't have the provinces yet.
        if (country) {
            this.country = country.long_name;
        }

        if (province) {
            this.province = province.long_name;
        }

        if (subRegion) {
            this.subRegion = subRegion.long_name;
        }

        this._generateNewSessionToken();
    }
    _deriveAddressFromPlace(origAddress, place) {
        // Ideally place.name would be good enough, but it will only return the whole street address if
        // Google Maps has the specific address in its street records, so some addresses will fail (eg new addresses).
        // In such 'failure' cases the place.name will often just be the street without the street number.

        // We need to avoid that, since we need to honour the person's address even if Google doesn't.
        // So the following is a hack to try and figure out if Google failed and if so then we try to add the
        // street number back onto the address.

        let streetNumberFound = this._extractAddressComponent(place.address_components, 'street_number');
        streetNumberFound = streetNumberFound && streetNumberFound.long_name;

        // Google's autocomplete didn't find a street_number component for the autocomplete address.
        if (!streetNumberFound) {
            // Parse out the missing text preceeding the found street.
            // There are other formats that could also be possible, but we're not currently handling them,
            // maybe we should but this is just doing the basic simple case for now.
            const indexSpace = place.name.indexOf(' '),
                startName = place.name.substring(0, indexSpace > 0 ? indexSpace : place.name.length),
                startAddress = origAddress.substring(0, origAddress.indexOf(startName)).trim();

            if (startAddress)
                return startAddress + ' ' + place.name;
        }

        // Otherwise we just trust Google and return the one it autocompleted.
        return place.name;
    }

    _onAddressChanged() {
        // Valid if something entered (or if not required).
        if (this.$.inputAddress.required) {
            const valid = this.$.inputAddress.value && this.$.inputAddress.value.length;
            this.$.inputAddress.invalid = !valid;
        }
        else this.$.inputAddress.invalid = false;

        this._updateInvalid();
    }
    _onCityChanged() {
        // Valid if something entered (or if not required).
        if (this.$.inputCity.required) {
            const valid = this.$.inputCity.value && this.$.inputCity.value.length;
            this.$.inputCity.invalid = !valid;
        }
        else this.$.inputCity.invalid = false;

        this._updateInvalid();
    }
    _onSubRegionChanged(v) {
        // If value is not a string then don't process at all,
        // instead make sure locationID is set.
        if (typeof v !== 'string') {
            if (this.subRegionLocationId > 0)
                this._observeSubRegionLocationId(this.subRegionLocationId);
            return;
        }

        // Location items not initialized yet.
        if (!this.$.inputSubRegion.items) return;

        const item = this._processLocationChanged(this.$.inputSubRegion);

        // --------------------------------------
        // Update locationId as appropriate.
        this.subRegionLocationId = item ? item.locationID : null;

        this._updateInvalid();
    }
    _onProvinceChanged(v) {
        // If value is not a string then don't process at all,
        // instead make sure locationID is set.
        if (typeof v !== 'string') {
            if (this.provinceLocationId > 0)
                this._observeProvinceLocationId(this.provinceLocationId);
            return;
        }

        // Location items not initialized yet.
        if (!this.$.inputProvince.items) return;

        const item = this._processLocationChanged(this.$.inputProvince);

        // --------------------------------------
        // Update locationId as appropriate.
        this.provinceLocationId = item ? item.locationID : null;

        this._updateInvalid();
    }
    _onPostalCodeChanged() {
        // Valid if something entered (or if not required).
        if (this.$.inputPostalCode.required) {
            const valid = this.$.inputPostalCode.value && this.$.inputPostalCode.value.length;
            this.$.inputPostalCode.invalid = !valid;
        }
        else this.$.inputPostalCode.invalid = false;

        this._updateInvalid();
    }
    _onCountryChanged(v) {
        // If value is not a string then don't process at all,
        // instead make sure locationID is set.
        if (typeof v !== 'string') {
            if (this.countryLocationId > 0)
                this._observeCountryLocationId(this.countryLocationId);
            return;
        }

        // Location items not initialized yet.
        if (!this.$.inputCountry.items) return;

        const item = this._processLocationChanged(this.$.inputCountry);

        // --------------------------------------
        // Update locationId as appropriate.
        this.countryLocationId = item ? item.locationID : null;

        // --------------------------------------
        // If an item is selected then we can use it to restrict other inputs, and to load the provinces.
        if (item) {
            if (this._autocompleters) {
                const restriction = { country: item.code };

                this._autocompleters.city.setComponentRestrictions(restriction);
                //this._autocompleters.address.setComponentRestrictions(restriction);
            }

            this._loadProvinces(item.locationID);
        }
        else {
            if (this._autocompleters) {
                this._autocompleters.city.setComponentRestrictions(null);
                //this._autocompleters.address.setComponentRestrictions(null);
            }

            this._loadProvinces(null);
        }

        this._updateInvalid();
    }
    _processLocationChanged(locationElement) {
        const locations = locationElement.items,
            hasLocations = locations && locations.length,
            text = locationElement.value ? locationElement.value.trim().toLowerCase() : '';
        let item = locationElement.selectedItem;

        // --------------------------------------
        // If an item in the locations list wasn't selected (ie the user just entered something manually)
        // then see if we can match a location in the list and auto select that.
        if (!item && text.length && hasLocations) {
            item = locations.find((i) => {
                // Match text to name or code (ignore accents, case, etc).
                const nameMatched = i.name.localeCompare(text, {}, { sensitivity: 'base' }) === 0,
                    codeMatched = i.code.localeCompare(text, {}, { sensitivity: 'base' }) === 0;

                return nameMatched || codeMatched;
            });

            if (item) {
                locationElement.selectedItem = item;
            }
        }

        // --------------------------------------
        // If required then check validity.
        if (locationElement.required) {
            // If there are locations available and item isn't set (ie a location isn't selected) then invalid.
            // Else no locations provided, then we allow text, if no text either then consider as invalid.
            // NOTE: Countries should always have locations and therefore should always require an item selection.
            if (hasLocations)
                locationElement.invalid = !item;
            else
                locationElement.invalid = !text.length;
        }
        else locationElement.invalid = false;

        if (locationElement && locationElement.id == "inputCountry") {  // when changing the country or province, we check if sub locations have been entered, if yes we reset.
            const country = this.$.inputCountry;
            const province = this.$.inputProvince;
            const subRegion = this.$.inputSubRegion;

            if (this.province != '' && (province.selectedItem && country.selectedItem && country.selectedItem.locationID != province.selectedItem.parentLocationID)) 
                this.province = '';


            if (this.subRegion != '' && (province.selectedItem && subRegion.selectedItem && subRegion.selectedItem.parentLocationID != province.selectedItem.locationID))
                this.subRegion = '';
        } else if (locationElement && locationElement.id == "inputProvince") {
            const province = this.$.inputProvince;
            const subRegion = this.$.inputSubRegion;

            if (this.subRegion != '' && (province.selectedItem && subRegion.selectedItem && subRegion.selectedItem.parentLocationID != province.selectedItem.locationID))
                this.subRegion = '';
        }

        return item;
    }

    _extractAddressComponent(address_components, type) {
        if (address_components) {
            for (const component of address_components) {
                if (component.types.includes(type)) {
                    return component;
                }
            }
        }

        return { long_name: null, short_name: null };
    }

    _computeCoords(place) {
        if (!place || !place.geometry) return null;

        // Return a google LatLng object with the coords.
        // See https://developers.google.com/maps/documentation/javascript/3.exp/reference#LatLng
        return place.geometry.location; // Google LatLng object.
    }

    _updateInvalid() {
        this.invalid =
            this.$.inputAddress.invalid ||
            this.$.inputCity.invalid ||
            this.$.inputSubRegion.invalid ||
            this.$.inputProvince.invalid ||
            this.$.inputPostalCode.invalid ||
            this.$.inputCountry.invalid;
    }

    clear(){
        this.address = '';
        this.addressUnit = '';
        this.city = '';
        this.subRegion = '';
        this.province = '';
        this.postalCode = '';
        this.country = '';
    }

    validate(){
        this.$.inputAddress.validate();
        this.$.inputCity.validate();
        this.$.inputSubRegion.validate();
        this.$.inputProvince.validate();
        this.$.inputPostalCode.validate();
        this.$.inputCountry.validate();

        this._updateInvalid();

        return !this.invalid;
    }
});